/* Adapted from the supplied Neon ReviewList: page groups, open/resolved
 * filters and status changes; authorship and access are server-owned. */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigationParams } from '../navigation/history'
import { apiFetch, ApiError } from '../../lib/api'
import { ErrorState } from '../state'
import { stampDateTime } from '../routeError'
import { commentHref, useReviewComments, type ReviewComment } from './api'
import './review-comments.css'

export default function ReviewCommentsPage() {
  const read = useReviewComments()
  const [params, setParams] = useNavigationParams()
  const requested = params.get('status')
  const filter = requested === 'resolved' || requested === 'all' ? requested : 'open'
  const setFilter = (status: 'open' | 'resolved' | 'all') =>
    setParams((previous) => {
      const next = new URLSearchParams(previous)
      if (status === 'open') next.delete('status')
      else next.set('status', status)
      return next
    })
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const key = useRef(read.key)
  key.current = read.key
  useEffect(() => {
    setBusy(null)
    setError('')
  }, [read.key])

  async function toggle(comment: ReviewComment) {
    if (busy) return
    const requestKey = read.key
    setBusy(comment.id)
    setError('')
    try {
      const saved = await apiFetch<ReviewComment>(`/api/review-comments/${comment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: comment.status === 'open' ? 'resolved' : 'open' }),
      })
      if (key.current === requestKey)
        read.update((items) => items.map((item) => (item.id === saved.id ? saved : item)))
    } catch {
      if (key.current === requestKey)
        setError('The comment status could not be updated. Try again.')
    } finally {
      if (key.current === requestKey) setBusy(null)
    }
  }

  const disabled = read.error instanceof ApiError && read.error.status === 404
  const shown = read.comments.filter((comment) => filter === 'all' || comment.status === filter)
  const pages = [...new Set(shown.map((comment) => comment.page))]
  return (
    <main className="detail review-comments-page" data-testid="review-comments-page">
      <header className="review-page-head">
        <h1>Comments</h1>
        <p>Product feedback from administrators and managers.</p>
      </header>
      {read.loading && <p role="status">Loading comments…</p>}
      {disabled ? (
        <p>Comments are not enabled on this installation.</p>
      ) : read.error ? (
        <ErrorState what="comments" error={read.error} onRetry={read.reload} />
      ) : null}
      {error && <p role="alert">{error}</p>}
      {!read.loading && !read.error && (
        <>
          <nav className="review-filters" aria-label="Comment status">
            {(['open', 'resolved', 'all'] as const).map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={filter === status}
                onClick={() => setFilter(status)}
              >
                {status === 'open' ? 'Open' : status === 'resolved' ? 'Resolved' : 'All'} (
                {status === 'all'
                  ? read.comments.length
                  : read.comments.filter((comment) => comment.status === status).length}
                )
              </button>
            ))}
          </nav>
          {!pages.length && <p>No comments in this view.</p>}
          {pages.map((page) => (
            <section className="review-page-group" key={page}>
              <h2>
                <Link to={page}>{page === '/v2' ? 'Today' : page}</Link>
              </h2>
              <ul>
                {shown
                  .filter((comment) => comment.page === page)
                  .map((comment) => (
                    <li
                      key={comment.id}
                      className={comment.status === 'resolved' ? 'is-resolved' : undefined}
                    >
                      <div>
                        <p className="review-meta">
                          {comment.author} · {stampDateTime(new Date(comment.createdAt))} ·{' '}
                          {comment.device}
                        </p>
                        {comment.anchor.snippet && (
                          <p className="review-snippet">On: “{comment.anchor.snippet}”</p>
                        )}
                        <p className="review-comment-text">{comment.text}</p>
                        <Link to={commentHref(comment)}>Open pinned spot ›</Link>
                      </div>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => toggle(comment)}
                      >
                        {busy === comment.id
                          ? 'Saving…'
                          : comment.status === 'open'
                            ? 'Mark done'
                            : 'Reopen'}
                      </button>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </main>
  )
}
